import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { Header } from '../landing/components/Header';
import { Footer } from '../landing/components/Footer';
import { MediaLightbox } from '../landing/components/MediaLightbox';
import { Accordion, Button } from '@/components';
import { useGetQuestions } from '@/services/queries/questions';
import { buildWhatsAppUrl, WHATSAPP_MESSAGE_B2B } from '@/constants/whatsapp';
import { ReclameAquiVerifiedSection } from './components/ReclameAquiVerifiedSection';

type LightboxMedia = { src: string; alt: string };

const BusinessLanding = () => {
	const { data: questionsData } = useGetQuestions();
	const [lightboxMedia, setLightboxMedia] = useState<LightboxMedia | null>(
		null,
	);

	const handleContact = () => {
		window.open(buildWhatsAppUrl(WHATSAPP_MESSAGE_B2B), '_blank');
	};

	const openLightbox = (media: LightboxMedia) => setLightboxMedia(media);
	const closeLightbox = () => setLightboxMedia(null);

	return (
		<section className="flex w-full flex-col items-center bg-brand-cream text-[#1c1c1c]">
			<Helmet>
				<title>
					Plumaa para empresas · NR-1 sem trabalho extra para o RH
				</title>
				<meta
					name="description"
					content="A Plumaa roda a NR-1 pelo seu RH: aplica os formulários, gera o dashboard, entrega o laudo médico e faz as palestras."
				/>
			</Helmet>

			<Header />

			<main className="w-full">
				<section className="relative flex min-h-[80vh] items-center overflow-hidden bg-[#0c0c0c] px-6 py-16 text-white md:px-10 md:py-20">
					<div className="pointer-events-none absolute left-1/2 top-[-235px] h-[720px] w-[960px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(225,29,116,0.22)_0%,transparent_60%)]" />
					<div className="relative z-10 mx-auto max-w-[760px]">
						<div className="text-center">
							<div className="font-albert mb-5 text-[11px] font-bold uppercase tracking-[0.22em] text-[#ff8aaa]">
								Plumaa para empresas
							</div>
							<h1 className="font-libre mb-6 text-[34px] font-bold leading-[1.08] tracking-[-0.015em] md:text-[60px]">
								Sua NR-1{' '}
								<em className="text-brand-pink">
									sem trabalho extra
								</em>{' '}
								para o RH.
							</h1>
							<p className="font-albert mx-auto mb-10 max-w-[560px] text-[18px] font-light leading-[1.7] text-white/70">
								A Plumaa aplica os formulários, gera o
								dashboard, entrega o laudo médico e roda as
								palestras. O seu RH só acompanha.
							</p>

							<Button
								onClick={handleContact}
								className="font-albert mx-auto flex justify-center rounded-lg bg-brand-pink px-10 py-4 text-[15px] font-bold normal-case tracking-[0.02em] text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-pink/90 hover:shadow-[0_14px_36px_rgba(225,29,116,0.4)]"
								text="Quero ver como funciona"
							/>
							<p className="font-albert mt-4 text-[13px] text-white/45">
								Setup em 24h · primeiro dashboard no mês 1
							</p>
						</div>
					</div>
				</section>

				<section className="bg-white px-6 py-20 md:px-10 md:py-24">
					<div className="mx-auto max-w-[880px] space-y-12 text-center">
						<div>
							<h4 className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-pink">
								O que a Plumaa faz pelo seu RH
							</h4>
							<h2 className="font-libre mb-5 text-[30px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[44px]">
								A norma é da empresa.
								<br />
								<em className="text-brand-pink">
									A operação é nossa.
								</em>
							</h2>
							<p className="font-albert mx-auto max-w-[600px] text-[17px] leading-[1.75] text-[#696969]">
								A norma exige pesquisa periódica, análise por
								setor, laudo médico e plano de ação. Isso vira
								rotina mensal - e quem implementa é o RH. A
								Plumaa entrega tudo pronto.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-[18px] text-left md:grid-cols-2">
							{[
								{
									num: 1,
									title: 'Aplicamos os formulários',
									desc: 'Disparo automático todo mês para 100% da equipe. Lembretes inclusos. O RH não envia nada.',
								},
								{
									num: 2,
									title: 'Geramos o dashboard',
									desc: 'Índice geral, análise por setor e alertas de risco. Atualizado conforme as respostas chegam.',
								},
								{
									num: 3,
									title: 'Entregamos o laudo médico',
									desc: 'Assinatura médica oficial, com CRM, anexável ao inventário NR-1 da sua empresa.',
								},
								{
									num: 4,
									title: 'Rodamos as palestras',
									desc: '2 palestras por mês com nossos especialistas, escolhidas pelos pontos críticos do dashboard.',
								},
							].map((item) => (
								<div
									key={item.num}
									className="flex items-start gap-[18px] rounded-[14px] border border-[#e4e4e0] bg-brand-cream px-7 py-6"
								>
									<div className="font-albert flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-pink text-[14px] font-bold text-white">
										{item.num}
									</div>
									<div>
										<h3 className="font-libre mb-1.5 text-[17px] font-bold leading-[1.3] text-[#1c1c1c]">
											{item.title}
										</h3>
										<p className="font-albert text-[14px] leading-[1.65] text-[#696969]">
											{item.desc}
										</p>
									</div>
								</div>
							))}
						</div>

						<div className="rounded-[14px] bg-[#0c0c0c] px-8 py-7 text-center">
							<p className="font-libre text-[17px] italic leading-[1.5] text-white">
								O RH cadastra a equipe uma vez. Depois disso,{' '}
								<span className="font-bold not-italic text-[#ff8aaa]">
									tudo roda sozinho
								</span>{' '}
								- e ele só abre o dashboard.
							</p>
						</div>
					</div>
				</section>

				<section className="bg-brand-cream px-6 py-20 md:px-10 md:py-24">
					<div className="mx-auto max-w-[880px]">
						<div className="mb-16 text-center">
							<h4 className="font-albert mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-pink">
								Na prática
							</h4>
							<h2 className="font-libre mb-4 text-[30px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[44px]">
								Veja como acontece
								<br />
								<em className="text-brand-pink">
									do lado de cá.
								</em>
							</h2>
							<p className="font-albert mx-auto max-w-[600px] text-[17px] leading-[1.75] text-[#696969]">
								Três telas. É isso que o seu colaborador vê, o
								que o seu RH abre, e o bônus que vem junto.
							</p>
						</div>

						<div className="mb-20 grid items-center gap-8 md:grid-cols-2 md:gap-14">
							<div>
								<p className="font-libre mb-3 text-[13px] font-bold uppercase tracking-[0.12em] text-brand-pink">
									Lado do colaborador
								</p>
								<h3 className="font-libre mb-3 text-[28px] font-bold leading-[1.2] tracking-[-0.01em] text-[#1c1c1c] md:text-[30px]">
									O formulário{' '}
									<em className="text-brand-pink">
										chega pronto.
									</em>
								</h3>
								<p className="font-albert mb-4 text-[15.5px] leading-[1.75] text-[#696969]">
									Todo mês cada colaborador recebe um link.
									Responde no navegador, em 5 minutos, de
									qualquer dispositivo. Quem não responde
									recebe lembrete automático.
								</p>
								<span className="font-albert inline-block rounded-md border border-brand-pink/20 bg-brand-pink/10 px-3 py-1.5 text-xs font-semibold text-[#c4194a]">
									Sem app · sem instalação · sem ação do RH
								</span>
							</div>
							<button
								type="button"
								onClick={() =>
									openLightbox({
										src: 'https://i.ibb.co/6c449FLj/questionario.gif',
										alt: 'Colaborador respondendo o formulário no navegador',
									})
								}
								aria-label="Ampliar demonstração do formulário"
								className="group relative block aspect-[16/10] w-full cursor-pointer overflow-hidden rounded-[14px] border border-[#e4e4e0] bg-white p-0 shadow-[0_18px_48px_rgba(0,0,0,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2"
							>
								<img
									src="https://i.ibb.co/6c449FLj/questionario.gif"
									alt="Colaborador respondendo o formulário no navegador"
									className="block h-full w-full scale-[1.03] object-cover"
								/>
								<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100 group-focus-visible:bg-black/40 group-focus-visible:opacity-100">
									<Maximize2
										className="h-10 w-10 text-white drop-shadow"
										strokeWidth={2.25}
										aria-hidden
									/>
								</div>
							</button>
						</div>

						<div className="mb-20 grid items-center gap-8 md:grid-cols-2 md:gap-14">
							<button
								type="button"
								onClick={() =>
									openLightbox({
										src: 'https://i.ibb.co/DPWszN3z/dashboard.gif',
										alt: 'Tour pelo dashboard do RH',
									})
								}
								aria-label="Ampliar tour pelo dashboard"
								className="group relative order-2 block aspect-[16/10] w-full cursor-pointer overflow-hidden rounded-[14px] border border-[#e4e4e0] bg-white p-0 shadow-[0_18px_48px_rgba(0,0,0,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2 md:order-1"
							>
								<img
									src="https://i.ibb.co/DPWszN3z/dashboard.gif"
									alt="Tour pelo dashboard do RH"
									className="block h-full w-full object-cover"
								/>
								<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100 group-focus-visible:bg-black/40 group-focus-visible:opacity-100">
									<Maximize2
										className="h-10 w-10 text-white drop-shadow"
										strokeWidth={2.25}
										aria-hidden
									/>
								</div>
							</button>
							<div className="order-1 md:order-2">
								<p className="font-libre mb-3 text-[13px] font-bold uppercase tracking-[0.12em] text-brand-pink">
									Lado do RH
								</p>
								<h3 className="font-libre mb-3 text-[28px] font-bold leading-[1.2] tracking-[-0.01em] text-[#1c1c1c] md:text-[30px]">
									O dashboard{' '}
									<em className="text-brand-pink">
										já está pronto.
									</em>
								</h3>
								<p className="font-albert mb-4 text-[15.5px] leading-[1.75] text-[#696969]">
									Índice geral da empresa, análise por setor,
									alertas de risco e evolução mês a mês.
									Atualizado sozinho. Na primeira entrega,
									nosso time guia o RH por cada indicador.
								</p>
								<span className="font-albert inline-block rounded-md border border-brand-pink/20 bg-brand-pink/10 px-3 py-1.5 text-xs font-semibold text-[#c4194a]">
									Análise por setor · call de leitura inclusa
								</span>
							</div>
						</div>

						<div className="grid items-center gap-8 md:grid-cols-2 md:gap-14">
							<div>
								<p className="font-libre mb-3 text-[13px] font-bold uppercase tracking-[0.12em] text-brand-pink">
									Bônus para a equipe
								</p>
								<h3 className="font-libre mb-3 text-[28px] font-bold leading-[1.2] tracking-[-0.01em] text-[#1c1c1c] md:text-[30px]">
									Terapia online{' '}
									<em className="text-brand-pink">
										a um clique.
									</em>
								</h3>
								<p className="font-albert mb-4 text-[15.5px] leading-[1.75] text-[#696969]">
									Todo colaborador entra automaticamente no
									clube de saúde mental Plumaa: terapia,
									médico e nutricionista online com desconto,
									agendamento direto pela plataforma. A
									empresa pode subsidiar - ou deixar livre.
								</p>
								<span className="font-albert inline-block rounded-md border border-brand-pink/20 bg-brand-pink/10 px-3 py-1.5 text-xs font-semibold text-[#c4194a]">
									Opcional para a empresa · custeado pelo
									colaborador ou pelo RH
								</span>
							</div>
							<button
								type="button"
								onClick={() =>
									openLightbox({
										src: 'https://i.ibb.co/kVTN7Jm4/agendar.gif',
										alt: 'Colaborador agendando terapia online',
									})
								}
								aria-label="Ampliar demonstração do agendamento"
								className="group relative block aspect-[16/10] w-full cursor-pointer overflow-hidden rounded-[14px] border border-[#e4e4e0] bg-white p-0 shadow-[0_18px_48px_rgba(0,0,0,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2"
							>
								<img
									src="https://i.ibb.co/kVTN7Jm4/agendar.gif"
									alt="Colaborador agendando terapia online"
									className="block h-full w-full scale-[1.03] object-cover"
								/>
								<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100 group-focus-visible:bg-black/40 group-focus-visible:opacity-100">
									<Maximize2
										className="h-10 w-10 text-white drop-shadow"
										strokeWidth={2.25}
										aria-hidden
									/>
								</div>
							</button>
						</div>
					</div>
				</section>

				<section className="border-y border-[#e4e4e0] bg-white px-6 py-16 text-center md:px-10 md:py-[72px]">
					<div className="mx-auto max-w-[720px] space-y-6">
						<h3 className="font-libre text-[28px] font-bold leading-[1.25] tracking-[-0.01em] text-[#1c1c1c] md:text-[32px]">
							Pronto para auditoria{' '}
							<em className="text-brand-pink">
								desde o primeiro mês.
							</em>
						</h3>
						<p className="font-albert mx-auto max-w-[580px] text-[15px] leading-[1.7] text-[#696969]">
							Instrumentos científicos validados (COPSOQ II-Br ·
							CBI), assinatura médica registrada no CRM-SP e
							histórico exportável em PDF. A fiscalização da NR-1
							começa em <strong>26/05/2026</strong> sem programa
							rodando, a empresa fica exposta a multas de R$ 1.800
							a R$ 6.700 por colaborador.
						</p>
						<div className="flex flex-wrap items-center justify-center gap-2 pt-1">
							{[
								'COPSOQ II-Br',
								'CBI',
								'Laudo com CRM',
								'Historico em PDF',
								'LGPD',
							].map((pill) => (
								<span
									key={pill}
									className="font-albert rounded-full border border-[#e4e4e0] bg-[#f5f5f2] px-3.5 py-1.5 text-[12.5px] font-medium text-[#1c1c1c]"
								>
									{pill}
								</span>
							))}
						</div>
					</div>
				</section>

				<section className="relative overflow-hidden bg-brand-pink px-6 py-20 text-center text-white md:px-10 md:py-24">
					<div className="pointer-events-none absolute left-1/2 top-[-150px] h-[500px] w-[600px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,transparent_65%)]" />
					<div className="relative z-10 mx-auto max-w-[600px] space-y-5">
						<h2 className="font-libre text-[30px] font-bold leading-[1.12] tracking-[-0.015em] md:text-[44px]">
							Tire a NR-1 da mesa do seu RH.
						</h2>
						<p className="font-albert mx-auto max-w-[480px] text-[16px] leading-[1.7] text-white/90">
							Em 24 horas sua empresa está cadastrada. No mês 1
							você já tem dashboard com dados reais.
						</p>
						<Button
							onClick={handleContact}
							className="font-albert mx-auto flex justify-center rounded-lg bg-white px-11 py-4 text-[15px] font-bold normal-case tracking-[0.02em] text-[#c4194a] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/95 hover:shadow-[0_14px_36px_rgba(0,0,0,0.2)]"
							text="Falar com a Plumaa"
						/>
						<p className="font-albert text-[12.5px] text-white/65">
							Conversa rápida no WhatsApp · sem compromisso
						</p>
					</div>
				</section>

				<section
					id="central-ajuda"
					className="w-full bg-brand-cream px-6 py-20 md:px-10 md:py-24"
				>
					<div className="mx-auto max-w-[880px] space-y-12">
						<div className="space-y-3 text-center">
							<h4 className="font-albert text-[11px] font-bold uppercase tracking-[0.22em] text-[#8a8a8a]">
								Informações importantes sobre o uso da
								plataforma.
							</h4>
							<h2 className="font-libre text-[30px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[44px]">
								Ficou com alguma dúvida?
							</h2>
						</div>
						{questionsData && (
							<Accordion
								questions={questionsData}
								itemsPerPage={5}
								variant="landing"
								className="px-0"
							/>
						)}
					</div>
				</section>

				<ReclameAquiVerifiedSection />
			</main>

			<Footer />

			<MediaLightbox
				isOpen={lightboxMedia !== null}
				onClose={closeLightbox}
				src={lightboxMedia?.src ?? ''}
				alt={lightboxMedia?.alt}
			/>
		</section>
	);
};

export { BusinessLanding };
